import type {
  GeoJSONFeature,
  Map as MapLibreMap,
  MapGeoJSONFeature,
} from "maplibre-gl";
import { EDA_SCALE } from "@/utils/helpers";
import { HexagonPriceEdaUpdate } from "@/api/admin";
import { PriceRangesI, tariffModeToLayer } from "@/types/base";
import type {
  EdaEditorTarget,
  EditorMode,
  PendingColorMap,
  PendingPriceMap,
  Point,
  PriceRangeMap,
  ScreenPoint,
} from "./types";

export const TEMPLATE_STOPS = [50, 100, 200, 500, 1000, 1500];
export const DEFAULT_COLOR = "#000";

export const isHexGeometry = (
  geometry:
    | GeoJSONFeature["geometry"]
    | MapGeoJSONFeature["geometry"]
    | undefined
): geometry is GeoJSON.Polygon | GeoJSON.MultiPolygon => {
  if (!geometry) return false;
  return geometry.type === "Polygon" || geometry.type === "MultiPolygon";
};

export const mapStopsToRange = (
  dataMin: number,
  dataMax: number,
  stops: number[]
) => {
  const origMin = Math.min(...stops);
  const origMax = Math.max(...stops);
  const origRange = origMax - origMin;
  const dataRange = dataMax - dataMin;

  return stops.map((stop) => {
    const normalized = (stop - origMin) / origRange;
    return dataMin + normalized * dataRange;
  });
};

export const priceToRangeColor = (
  price: number,
  min: number,
  max: number,
  colors: readonly string[],
  stops: number[]
) => {
  if (min === max) return colors[0];

  const mappedStops = mapStopsToRange(min, max, stops);

  for (let i = 0; i < mappedStops.length; i += 1) {
    if (price <= mappedStops[i]) {
      return colors[i];
    }
  }

  return colors[colors.length - 1];
};

export const getPriceRangesMap = (
  priceRanges: PriceRangesI[] | undefined,
  target: EdaEditorTarget
): PriceRangeMap => {
  if (!priceRanges) return {};

  const next: PriceRangeMap = {};
  priceRanges.forEach((item) => {
    const range = item.ranges[target];
    next[item.id] =
      range && range.min != null && range.max != null
        ? { min: 0, max: range.max }
        : { min: 100, max: 800 };
  });

  return next;
};

export const getPendingColorMap = (
  pendingUpdates: PendingPriceMap,
  featureHexgroupMap: Record<number, number>,
  priceRangesMap: PriceRangeMap
): PendingColorMap => {
  const next: PendingColorMap = {};

  Object.entries(pendingUpdates).forEach(([id, price]) => {
    const numericId = Number(id);
    const hexgroupId = featureHexgroupMap[numericId];
    const range = hexgroupId ? priceRangesMap[hexgroupId] : null;

    if (!range) return;

    next[numericId] = priceToRangeColor(
      price,
      range.min,
      range.max,
      EDA_SCALE,
      TEMPLATE_STOPS
    );
  });

  return next;
};

export const getEffectivePriceRangesMap = (
  baseRanges: PriceRangeMap,
  pendingUpdates: PendingPriceMap,
  featureHexgroupMap: Record<number, number>
): PriceRangeMap => {
  const next: PriceRangeMap = Object.fromEntries(
    Object.entries(baseRanges).map(([id, range]) => [Number(id), { ...range }])
  );

  Object.entries(pendingUpdates).forEach(([featureId, price]) => {
    const hexgroupId = featureHexgroupMap[Number(featureId)];
    if (!hexgroupId) return;

    const currentRange = next[hexgroupId] || { min: price, max: price };
    next[hexgroupId] = {
      min: 0,
      max: Math.max(currentRange.max, price),
    };
  });

  return next;
};

export const getCenter = (feature: MapGeoJSONFeature): Point | null => {
  const { geometry } = feature;
  if (!isHexGeometry(geometry)) return null;

  const ring =
    geometry.type === "Polygon"
      ? (geometry.coordinates?.[0] as Point[]) || null
      : (geometry.coordinates?.[0]?.[0] as Point[]) || null;
  if (!ring?.length) return null;

  const points = ring.length > 1 ? ring.slice(0, -1) : ring;
  const total = points.reduce<Point>(
    (acc, [lng, lat]) => [acc[0] + lng, acc[1] + lat],
    [0, 0]
  );

  return [total[0] / points.length, total[1] / points.length];
};

export const planarDistance = ([lngA, latA]: Point, [lngB, latB]: Point) => {
  const avgLat = ((latA + latB) / 2) * (Math.PI / 180);
  const dx = (lngA - lngB) * Math.cos(avgLat);
  const dy = latA - latB;

  return Math.sqrt(dx * dx + dy * dy);
};

export const getNumericFeatureProperty = (
  feature: MapGeoJSONFeature,
  key: "id" | "hexgroup_id" | "editor_zone_id"
) => {
  const rawId = feature.properties?.[key];
  const id = Number(rawId);
  return Number.isFinite(id) ? id : null;
};

export const isHexFeature = (
  feature: GeoJSONFeature | MapGeoJSONFeature | undefined
): feature is MapGeoJSONFeature =>
  Boolean(feature && isHexGeometry(feature.geometry) && feature.properties);

export const dedupeFeatures = (
  features: Array<GeoJSONFeature | MapGeoJSONFeature>
) => {
  const map = new Map<number, MapGeoJSONFeature>();

  features.forEach((feature) => {
    if (!isHexFeature(feature)) return;

    const id = getNumericFeatureProperty(feature, "id");
    if (id === null || map.has(id)) return;
    map.set(id, feature);
  });

  return Array.from(map.values());
};

export const mergeSelections = (
  current: MapGeoJSONFeature[],
  next: MapGeoJSONFeature[]
) => dedupeFeatures([...current, ...next]);

export const removeSelection = (
  current: MapGeoJSONFeature[],
  next: MapGeoJSONFeature[]
) => {
  const idsToRemove = new Set(
    next
      .map((feature) => getNumericFeatureProperty(feature, "id"))
      .filter((id): id is number => id !== null)
  );

  return current.filter((feature) => {
    const id = getNumericFeatureProperty(feature, "id");
    return id === null || !idsToRemove.has(id);
  });
};

export const buildSavedZoneSelection = (
  clickedFeature: MapGeoJSONFeature,
  features: MapGeoJSONFeature[]
) => {
  const editorZoneId = getNumericFeatureProperty(
    clickedFeature,
    "editor_zone_id"
  );
  if (editorZoneId === null) return [clickedFeature];

  return features.filter(
    (feature) =>
      getNumericFeatureProperty(feature, "editor_zone_id") === editorZoneId
  );
};

export const buildSelection = (
  clickedFeature: MapGeoJSONFeature,
  features: MapGeoJSONFeature[]
) => {
  const clickedId = getNumericFeatureProperty(clickedFeature, "id");
  const clickedCenter = getCenter(clickedFeature);

  if (clickedId === null || !clickedCenter) {
    return [clickedFeature];
  }

  const centers = new Map<number, Point>();
  features.forEach((feature) => {
    const id = getNumericFeatureProperty(feature, "id");
    const center = getCenter(feature);
    if (id === null || !center) return;
    centers.set(id, center);
  });

  const nearestDistance = features
    .map((feature) => {
      const id = getNumericFeatureProperty(feature, "id");
      const center = id === null ? null : centers.get(id);
      if (!center || id === clickedId) return Number.POSITIVE_INFINITY;
      return planarDistance(clickedCenter, center);
    })
    .reduce((min, value) => Math.min(min, value), Number.POSITIVE_INFINITY);

  if (!Number.isFinite(nearestDistance)) {
    return [clickedFeature];
  }

  const adjacencyThreshold = nearestDistance * 1.3;
  const featuresById = new Map(
    features
      .map((feature) => {
        const id = getNumericFeatureProperty(feature, "id");
        return id === null ? null : ([id, feature] as const);
      })
      .filter(Boolean) as Array<readonly [number, MapGeoJSONFeature]>
  );
  const visited = new Set<number>([clickedId]);
  const queue: Array<{ id: number; depth: number }> = [
    { id: clickedId, depth: 0 },
  ];

  while (queue.length) {
    const current = queue.shift();
    if (!current || current.depth >= 2) continue;

    const currentCenter = centers.get(current.id);
    if (!currentCenter) continue;

    centers.forEach((candidateCenter, candidateId) => {
      if (visited.has(candidateId)) return;
      if (
        planarDistance(currentCenter, candidateCenter) <= adjacencyThreshold
      ) {
        visited.add(candidateId);
        queue.push({ id: candidateId, depth: current.depth + 1 });
      }
    });
  }

  return Array.from(visited)
    .map((id) => featuresById.get(id))
    .filter(Boolean) as MapGeoJSONFeature[];
};

export const getSelectionDraftPrice = (
  selection: MapGeoJSONFeature[],
  pendingUpdates: PendingPriceMap,
  priceField: string
) => {
  const firstId = selection[0]
    ? getNumericFeatureProperty(selection[0], "id")
    : null;
  const localPrice = firstId !== null ? pendingUpdates[firstId] : undefined;
  const sourcePrice = selection[0]?.properties?.[priceField];

  if (localPrice !== undefined) return String(localPrice);
  if (sourcePrice !== undefined) return String(sourcePrice);
  return "";
};

export const mergeFeatureHexgroups = (
  prev: Record<number, number>,
  selection: MapGeoJSONFeature[]
) => {
  const next = { ...prev };

  selection.forEach((feature) => {
    const id = getNumericFeatureProperty(feature, "id");
    const hexgroupId = getNumericFeatureProperty(feature, "hexgroup_id");
    if (id !== null && hexgroupId !== null) {
      next[id] = hexgroupId;
    }
  });

  return next;
};

export const applyLocalPriceToSelection = (
  selection: MapGeoJSONFeature[],
  price: number,
  priceField: string
): MapGeoJSONFeature[] =>
  selection.map((feature) => {
    const nextFeature = Object.assign(
      Object.create(Object.getPrototypeOf(feature)),
      feature
    ) as MapGeoJSONFeature;

    nextFeature.properties = {
      ...feature.properties,
      [priceField]: price,
    };

    return nextFeature;
  });

export const applyLocalEditorZoneToSelection = (
  selection: MapGeoJSONFeature[],
  editorZoneId: number | null
): MapGeoJSONFeature[] =>
  selection.map((feature) => {
    const nextFeature = Object.assign(
      Object.create(Object.getPrototypeOf(feature)),
      feature
    ) as MapGeoJSONFeature;

    nextFeature.properties = {
      ...feature.properties,
      editor_zone_id: editorZoneId,
    };

    return nextFeature;
  });

export const mergePendingUpdates = (
  prev: PendingPriceMap,
  ids: number[],
  price: number
): PendingPriceMap => {
  const next = { ...prev };
  ids.forEach((id) => {
    next[id] = price;
  });
  return next;
};

export const getPendingPayload = (
  pendingUpdates: PendingPriceMap
): HexagonPriceEdaUpdate[] =>
  Object.entries(pendingUpdates).map(([id, price]) => ({
    id: Number(id),
    price,
  }));

export const getPriceField = (target: EdaEditorTarget) =>
  tariffModeToLayer[target];

export const getScreenPoint = (
  feature: MapGeoJSONFeature,
  map: MapLibreMap
): ScreenPoint | null => {
  const center = getCenter(feature);
  if (!center) return null;

  const projected = map.project(center);
  return { x: projected.x, y: projected.y };
};

export const isPointInPolygon = (
  point: ScreenPoint,
  polygon: ScreenPoint[]
): boolean => {
  if (polygon.length < 3) return false;

  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const current = polygon[i];
    const previous = polygon[j];
    const intersects =
      current.y > point.y !== previous.y > point.y &&
      point.x <
        ((previous.x - current.x) * (point.y - current.y)) /
          (previous.y - current.y) +
          current.x;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

export const getLassoSelection = (
  features: MapGeoJSONFeature[],
  polygon: ScreenPoint[],
  map: MapLibreMap
) =>
  features.filter((feature) => {
    const screenPoint = getScreenPoint(feature, map);
    return screenPoint ? isPointInPolygon(screenPoint, polygon) : false;
  });

export const getEditorModeLabel = (mode: EditorMode) =>
  mode === "lasso" ? "Лассо" : "Клик";
